import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TwoFactorVerification } from '../../components/auth/TwoFactorVerification';
import { authApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';

export default function TwoFactorVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, setUser, logout } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user came from login (has user data in location state)
  const fromLogin = location.state?.fromLogin;
  const userData = location.state?.userData;

  useEffect(() => {
    // If not coming from login or no token, redirect to login
    if (!fromLogin || !token) {
      navigate('/auth/login');
      return;
    }

    // If user doesn't have 2FA enabled, redirect to dashboard
    if (!userData?.google_auth_enabled) {
      navigate('/dashboard');
      return;
    }
  }, [fromLogin, token, userData, navigate]);

  const handleVerify = async (code: string): Promise<boolean> => {
    if (!token) {
      setError('Authentication token not found');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await authApi.verifyTwoFactorCode(token, code);

      if (response.success && response.data?.is_valid) {
        // 2FA verification successful
        // Update user data in store if needed
        if (userData) {
          setUser(userData);
        }
        
        // Redirect to intended destination or dashboard
        const intendedPath = location.state?.intendedPath || '/dashboard';
        navigate(intendedPath, { replace: true });
        return true;
      } else {
        setError(response.message || 'Invalid verification code');
        return false;
      }
    } catch (err) {
      setError('Network error occurred. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Logout user and redirect to login
    logout();
    navigate('/auth/login');
  };

  // Don't render if redirecting
  if (!fromLogin || !token || !userData?.google_auth_enabled) {
    return null;
  }

  return (
    <TwoFactorVerification
      onVerify={handleVerify}
      onCancel={handleCancel}
      isLoading={isLoading}
      error={error}
      title="Two - factor authentication"
      subtitle="Enter the 6 figure confirmation code shown on the app"
    />
  );
}
